import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoadingService } from './loading.service';
import { MessageErrorResolver } from '../../utilities/message-error-resolver';
import { EMPTY, Observable, catchError, map, mapTo, tap } from 'rxjs';
import { IPage } from '../../models/pagination/page';
import { ResponseDto, ResponseWithErrorDto } from '../../models/exceptions/response-dto';
import { environment } from 'src/environments/environment';
import { ChampionshipDto } from '../../models/championship-dto';
import { ICompetitionDto } from '../../models/competition-dto';
import { ICategoryDto } from '../../models/category-dto';

import { CompetitionPostForm } from '../../models/form/competition-post-form';
import { CompetitionFilter } from '@models/events/competition/competition-filter';
import { toParams } from '../../utilities/utility-functions';
import { CompetitionPutForm } from '@models/form/put/competition-put-form';

@Injectable({
  providedIn: 'root'
})
export class ChampionshipService {

  private url: string = environment.apiUrl + "/v1/competicoes";

  constructor(private httpClient: HttpClient,
    private loadingService: LoadingService,
    private messageErrorResolver: MessageErrorResolver) { }

   /**
   * @deprecated Use `searchCompetitions()` instead.
   */
  public getAllChampionships(page?: number, size?: number): Observable<IPage<ChampionshipDto>> {
    let params = new HttpParams();
    if (page) params = params.set("page", page);
    if (size) params = params.set("size", size);
    return this.httpClient.get<ResponseDto<IPage<ChampionshipDto>>>(this.url, {params})
      .pipe(
        map(response => response.response),
        catchError((error: ResponseWithErrorDto) => {
          this.messageErrorResolver.resolveMessage(error);
          return EMPTY;
        })
      );
  }


  persist(form: CompetitionPostForm): Observable<ICompetitionDto> {
    this.loadingService.activate();
    return this.httpClient.post<ResponseDto<ICompetitionDto>>(this.url, form)
      .pipe(
        map(response => response.response),
        catchError(err => {
          this.loadingService.deactivate();
          throw err;
        }),
        tap(() => this.loadingService.deactivate())
      );
  }

  update(form: CompetitionPutForm): Observable<CompetitionPutForm> {
    this.loadingService.activate();
    return this.httpClient.put<ResponseDto<CompetitionPutForm>>(this.url, form)
      .pipe(
        map(response => response.response),
        catchError(err => {
          this.loadingService.deactivate();
          throw err;
        }),
        tap(() => this.loadingService.deactivate())
      );
  }

  public getAllCategoryByCompetition(comNrId: number, page?: number, size?: number, catTxNome?: string): Observable<IPage<ICategoryDto>> {
    let params = toParams([comNrId,page,size])
    if (page) params = params.set("page", page);
    if (size) params = params.set("size", size);
    if (catTxNome) params = params.set("catTxNome", catTxNome);
    return this.httpClient.get<ResponseDto<IPage<ICategoryDto>>>(this.url + `/${comNrId}/categorias`, {params})
      .pipe(
        map(response => response.response),
        catchError((error: ResponseWithErrorDto) => {
          this.messageErrorResolver.resolveMessage(error);
          return EMPTY;
        })
      );
  }

  searchCompetitions(filters?: CompetitionFilter, page: number = 0, size: number = 10): Observable<IPage<ICompetitionDto>> {
      let params = new HttpParams();
      if (size) params = params.set("size", size);
      if (page >= 0) params = params.set("page", page);
      if (filters?.eveNrId) params = params.set("eveNrId", filters.eveNrId);
      if (filters?.bloNrId) params = params.set("bloNrId", filters.bloNrId);
      if (filters?.comTxNome) params = params.set("comTxNome", filters.comTxNome);
      if (filters?.comTxDescricao) params = params.set("comTxDescricao", filters.comTxDescricao);
      if (filters?.espNrId) params = params.set("espNrId", filters.espNrId);

      return this.httpClient.get<ResponseDto<IPage<ICompetitionDto>>>(this.url+"/search", {params})
        .pipe(
          map(response => response.response ),
          catchError((error: ResponseWithErrorDto) => {
            this.loadingService.deactivate();
            this.messageErrorResolver.resolveMessage(error);
            return EMPTY;
          }),
        );
  }

  getCompetitionById(idCometition: number): Observable<ICompetitionDto> {
    let params = new HttpParams();
    params.set("comNrId", idCometition);
    return this.httpClient.get<ResponseDto<ICompetitionDto>>(this.url + `/${idCometition}/completa`, {params})
    .pipe(
      map(response => response.response ),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  getCategoriesByCompetitionId(idCometition: number): Observable<IPage<ICategoryDto>> {
    let params = new HttpParams();
    params.set("comNrId", idCometition);
    return this.httpClient.get<ResponseDto<IPage<ICompetitionDto>>>(this.url + `/${idCometition}/categorias`, {params})
    .pipe(
      map(response => response.response ),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  getCompleteCategoriesByCompetitionId(idCometition: number): Observable<IPage<ICategoryDto>> {
    let params = new HttpParams();
    params.set("comNrId", idCometition);
    return this.httpClient.get<ResponseDto<IPage<ICompetitionDto>>>(this.url + `/${idCometition}/categorias/completa`, {params})
    .pipe(
      map(response => response.response ),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  checkIfSportNeedBelt(comNrId: number): Observable<boolean> {
    return this.httpClient.get<ResponseDto<boolean>>(`${this.url}/${comNrId}/precisa-gub`)
    .pipe(
      map(response => response.response ),
      catchError((error: ResponseWithErrorDto) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(error);
        return EMPTY;
      }),
    );
  }

  deleteCompetition(comNrId:number):Observable<void>{
      return this.httpClient.delete<ResponseDto<void>>(`${this.url}/${comNrId}/deletar-competicao`)
      .pipe(map(() => undefined));
  }
}
